import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { generateWorkflowBotWizard } from '../../../../../api'
import { useAsyncCallback, useInitData } from '../../../../../hooks'
import { isApiPaymentRequiredStatus } from '../../../../../utils/api'
import {
  getAIWizardCardStageEntries,
  getAIWizardGenericErrorNotification,
  getAIWizardInitStageNextValues,
  getAIWizardLimitReachedErrorNotification,
  getAIWizardPayload,
} from '../../RequestWizardAIWizard.utils'
import { REQUEST_WIZARD_AI_WIZARD_NOTIFICATION_ID } from '../../RequestWizardForm.constants'
import {
  isRequestWizardFinished,
  isRequestWizardInitStage,
} from '../../RequestWizardForm.utils'

function useRequestWizardAIWizard({
  chunk,
  formikRef,
  messageUuid,
  stage,
  uuid,
  onRemoveNotification,
  onSetNotification,
}) {
  const { t } = useTranslation()

  const { ai_features: aiFeatures } = useInitData()

  const [isLoading, setIsLoading] = useState(false)
  const [isFilled, setIsFilled] = useState(false)
  const [prevChunk, setPrevChunk] = useState(chunk)

  // A new step always starts un-filled.
  if (chunk !== prevChunk) {
    setPrevChunk(chunk)
    setIsFilled(false)
  }

  // On the final card stage there are no input fields to fill, so there is
  // nothing for the AI Wizard to do.
  const isWorkflowFinished = isRequestWizardFinished({ chunk, stage })

  const showButton =
    !!messageUuid &&
    !!aiFeatures?.workflow_wizard_generation &&
    !isWorkflowFinished

  function applyResponse(responseData) {
    if (!formikRef.current) {
      return false
    }

    const { setFieldValue, setValues, values } = formikRef.current
    const suggestions = responseData?.fields ?? responseData

    if (isRequestWizardInitStage(stage)) {
      const nextValues = getAIWizardInitStageNextValues({
        suggestions,
        values,
      })

      if (Object.keys(nextValues).length === 0) {
        return false
      }

      setValues(prev => ({ ...prev, ...nextValues }))

      return true
    }

    const entries = getAIWizardCardStageEntries({
      chunk,
      suggestions,
      values,
    })

    entries.forEach(([fieldName, value]) => {
      setFieldValue(fieldName, value)
    })

    return entries.length > 0
  }

  const generateWizard = useAsyncCallback(
    () => generateWorkflowBotWizard(getAIWizardPayload({ messageUuid, uuid })),
    [messageUuid, uuid]
  )

  function handleGenerate() {
    onRemoveNotification(REQUEST_WIZARD_AI_WIZARD_NOTIFICATION_ID)
    setIsFilled(false)
    setIsLoading(true)

    generateWizard()
      .then(({ data }) => {
        setIsFilled(applyResponse(data))
      })
      .catch(error => {
        const notification = isApiPaymentRequiredStatus(error?.response?.status)
          ? getAIWizardLimitReachedErrorNotification({ t })
          : getAIWizardGenericErrorNotification({ t })

        onSetNotification(notification)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return { handleGenerate, isFilled, isLoading, showButton }
}

export default useRequestWizardAIWizard
